"use client";

import React, { useEffect, useRef, useState } from "react";
import { Loader, Check } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
function InnerCircleForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="bg-primary-foreground/10 border border-primary-foreground/20 rounded-lg p-8 text-center animate-in fade-in zoom-in duration-500">
        <div className="flex justify-center mb-4">
          <div className="bg-secondary text-secondary-foreground rounded-full p-3">
            <Check className="size-8" />
          </div>
        </div>
        <h3 className="text-2xl font-serif font-bold text-white mb-2">
          Welcome to the Inner Circle
        </h3>
        <p className="text-primary-foreground/80 text-lg">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary-foreground/90">
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" {...field} className="bg-primary-foreground/5 border-primary-foreground/20 text-white placeholder:text-primary-foreground/40 focus-visible:ring-white/50 focus-visible:border-white h-12" />
              </FormControl>
              <FormMessage className="text-red-200" />
            </FormItem>} />

        {}
        <FormField control={control} name="email_address" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary-foreground/90">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" {...field} className="bg-primary-foreground/5 border-primary-foreground/20 text-white placeholder:text-primary-foreground/40 focus-visible:ring-white/50 focus-visible:border-white h-12" />
              </FormControl>
              <FormMessage className="text-red-200" />
            </FormItem>} />

        {}
        <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary-foreground/90">
                Phone Number <span className="text-xs opacity-70">(Optional)</span>
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 123-4567" {...field} className="bg-primary-foreground/5 border-primary-foreground/20 text-white placeholder:text-primary-foreground/40 focus-visible:ring-white/50 focus-visible:border-white h-12" />
              </FormControl>
              <FormMessage className="text-red-200" />
            </FormItem>} />

        {}
        <FormField control={control} name="interest_category" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary-foreground/90">
                I’m Interested In
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <select {...field} className="w-full h-12 px-3 py-2 bg-primary-foreground/5 border border-primary-foreground/20 rounded-md text-white placeholder:text-primary-foreground/40 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white appearance-none">
                    <option value="" disabled className="text-gray-500">
                      Select a category
                    </option>
                    {["Antique Furniture", "Vintage Jewelry", "Collectible Art", "Historical Documents", "All of the Above"].map((option, i) => <option key={option} value={option} data-index={i} className="text-foreground bg-background">
                        {option}
                      </option>)}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none text-white/60">
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                      <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" fillRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage className="text-red-200" />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="agree_terms" rules={{
      required: "You must agree to the terms"
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md p-2">
            <FormControl>
              <input type="checkbox" checked={field.value} onChange={field.onChange} className="mt-1 size-4 rounded border-primary-foreground/30 bg-primary-foreground/10 text-primary focus:ring-white/50 focus:ring-offset-0" />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-primary-foreground/90 font-normal">
                I agree to receive communications and accept the privacy policy.
              </FormLabel>
              <FormMessage className="text-red-200" />
            </div>
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-500/20 border border-red-200/30 rounded-lg">
          <p className="text-red-100 text-sm">{submitError}</p>
        </div>}

      <div className="pt-2">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[200px] bg-foreground text-background hover:bg-foreground/90 h-12 text-lg font-bold shadow-lg transition-transform hover:scale-[1.02]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Joining...
            </> : "Subscribe"}
        </Button>
      </div>
    </Form>;
}
export default function JoinTheInnerCircle() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="join-the-inner-circle" className="bg-primary py-24 relative overflow-hidden">
      <style>{`
        #join-the-inner-circle {
          --secondary: hsl(215 10% 70%);
          --secondary-foreground: hsl(215 10% 10%);
          --chart-2: hsl(215 10% 70%);
          --chart-4: hsl(215 10% 70%);
        }
        .dark #join-the-inner-circle {
          --secondary: hsl(215 10% 30%);
          --secondary-foreground: hsl(215 10% 90%);
          --chart-2: hsl(215 10% 30%);
          --chart-4: hsl(215 10% 30%);
        }
      `}</style>
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-10">
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-secondary/40 rounded-full blur-3xl" />
        <div className="absolute top-1/2 right-0 w-64 h-64 bg-secondary rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={`max-w-3xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4 tracking-tight">
              Join the Inner Circle
            </h2>
            <p className="text-primary-foreground/80 text-xl font-sans max-w-2xl mx-auto">
              Exclusive access to rare acquisitions, private viewings, and
              expert insights before they reach the public.
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-secondary/20 rounded-xl p-6 md:p-10 shadow-2xl">
            <FormProvider formId="inner_circle_signup_home" sectionName="join-the-inner-circle" defaultValues={{
            full_name: "",
            email_address: "",
            phone_number: "",
            interest_category: "",
            agree_terms: false
          }} validationRules={{
            full_name: {
              required: true
            },
            email_address: {
              required: true,
              pattern: "email"
            },
            agree_terms: {
              required: true
            }
          }} submitText="Subscribe" successMessage="Thank you for joining the Inner Circle. We will be in touch shortly.">
              <InnerCircleForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}